import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import api from '../../api';
import CustomPagination from '../Pagination';
import { ColorSwatchLabel } from '../HtmlColorField';
import { formatDate } from '../../helpers/formatters';
import { filamentLabel, formatGrams } from '../../helpers/filaments';
import { formatProductionDurationMinutes, statusLabel } from '../../helpers/jobs';

const PAGE_SIZE = 10;

function DetailField({ label, children }) {
    return (
        <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600} component="div">
                {children ?? '—'}
            </Typography>
        </Grid>
    );
}

/**
 * Detalhes da impressão dentro da linha expandida do histórico. O carretel que
 * está sendo consultado aparece destacado entre os filamentos da impressão.
 */
function JobDetails({ state, filamentId }) {
    if (!state || state.isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
            </Box>
        );
    }
    if (state.error) {
        return <Alert severity="error" sx={{ my: 1 }}>{state.error}</Alert>;
    }

    const job = state.data || {};
    const objects = `${job.printed_count ?? 0} de ${job.object_count ?? 0}`;
    const filaments = Array.isArray(job.filaments) ? job.filaments : [];

    return (
        <Box sx={{ py: 2, px: 1 }}>
            <Grid container spacing={2}>
                <DetailField label="Início">{formatDate(job.started_at)}</DetailField>
                <DetailField label="Fim">{formatDate(job.finished_at)}</DetailField>
                <DetailField label="Duração estimada">
                    {job.estimated_duration_minutes > 0
                        ? formatProductionDurationMinutes(job.estimated_duration_minutes)
                        : null}
                </DetailField>
                <DetailField label="Filamento estimado">
                    {job.estimated_filament_consumption_grams > 0
                        ? formatGrams(job.estimated_filament_consumption_grams)
                        : null}
                </DetailField>
                {job.actual_filament_consumption_grams != null && (
                    <DetailField label="Consumo real">
                        {formatGrams(job.actual_filament_consumption_grams)}
                    </DetailField>
                )}
                <DetailField label="Objetos impressos">{objects}</DetailField>
                {job.status_reason && (
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Motivo do status
                        </Typography>
                        <Typography variant="body2">{job.status_reason}</Typography>
                    </Grid>
                )}
            </Grid>

            {filaments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Filamentos desta impressão
                    </Typography>
                    {filaments.map((item, index) => {
                        const isCurrent = item.filament_uuid && item.filament_uuid === filamentId;
                        return (
                            <Box
                                key={item.filament_uuid || `${item.color}-${index}`}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
                            >
                                <Typography
                                    variant="body2"
                                    component="div"
                                    fontWeight={isCurrent ? 700 : 400}
                                >
                                    <ColorSwatchLabel
                                        color={item.color}
                                        label={filamentLabel(item) || item.color_label || item.color}
                                    />
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatGrams(item.grams)}
                                </Typography>
                                {isCurrent && <Chip size="small" color="primary" label="este carretel" />}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

/**
 * Histórico de consumo de um carretel: quais impressões o usaram e quanto cada
 * uma consumiu.
 */
function FilamentUsageDialog({ open, onClose, filament }) {
    const [usages, setUsages] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalGrams, setTotalGrams] = useState(0);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [expandedJob, setExpandedJob] = useState('');
    // Os detalhes de cada impressão só são buscados quando a linha é aberta, e
    // ficam guardados para reabrir sem novo request.
    const [jobDetails, setJobDetails] = useState({});

    const filamentId = filament?.id || filament?.uuid || '';

    const toggleJob = useCallback((jobUUID) => {
        if (!jobUUID) {
            return;
        }
        setExpandedJob((current) => (current === jobUUID ? '' : jobUUID));
        setJobDetails((current) => {
            if (current[jobUUID]) {
                return current;
            }
            api.get(`jobs/${jobUUID}`)
                .then((response) => {
                    setJobDetails((prev) => ({
                        ...prev,
                        [jobUUID]: { isLoading: false, data: response.data?.data || {} },
                    }));
                })
                .catch(() => {
                    setJobDetails((prev) => ({
                        ...prev,
                        [jobUUID]: { isLoading: false, error: 'Não foi possível carregar os detalhes desta impressão.' },
                    }));
                });
            return { ...current, [jobUUID]: { isLoading: true } };
        });
    }, []);

    const fetchUsages = useCallback(() => {
        if (!open || !filamentId) {
            return;
        }
        setIsLoading(true);
        setError('');
        api.get(`filaments/${filamentId}/usages`, { params: { page, limit: PAGE_SIZE } })
            .then((response) => {
                setUsages(response.data.usages || []);
                setTotalPages(response.data.total_pages || 1);
                setTotalGrams(response.data.total_grams || 0);
                setTotal(response.data.total || 0);
            })
            .catch(() => {
                setError('Não foi possível carregar o histórico de uso deste filamento.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [open, filamentId, page]);

    useEffect(() => {
        fetchUsages();
    }, [fetchUsages]);

    useEffect(() => {
        if (open) {
            setPage(1);
        }
    }, [open, filamentId]);

    useEffect(() => {
        setExpandedJob('');
    }, [page, filamentId, open]);

    return (
        <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                    <Typography variant="h6" component="div">
                        Histórico de uso
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {filamentLabel(filament) || 'Filamento'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label="fechar">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!isLoading && error && <Alert severity="error">{error}</Alert>}

                {!isLoading && !error && usages.length === 0 && (
                    <Alert severity="info">
                        Este filamento ainda não foi usado em nenhuma impressão.
                    </Alert>
                )}

                {!isLoading && !error && usages.length > 0 && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {total === 1 ? '1 impressão' : `${total} impressões`}
                            {' · '}
                            {formatGrams(totalGrams)} nesta página
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 48 }} />
                                        <TableCell sx={{ fontWeight: 'bold' }}>Impressão</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Impressora</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Consumo</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Registrado em</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {usages.map((usage) => {
                                        const isExpanded = expandedJob === usage.job_uuid;
                                        return (
                                            <React.Fragment key={usage.uuid}>
                                                <TableRow
                                                    hover
                                                    onClick={() => toggleJob(usage.job_uuid)}
                                                    // A linha aberta muda de fundo: com várias
                                                    // linhas, sem isso não se enxerga a qual
                                                    // impressão os detalhes pertencem.
                                                    sx={{
                                                        cursor: usage.job_uuid ? 'pointer' : 'default',
                                                        bgcolor: isExpanded ? 'action.selected' : undefined,
                                                        '& > *': {
                                                            borderBottom: isExpanded ? 'unset' : undefined,
                                                            fontWeight: isExpanded ? 600 : undefined,
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            disabled={!usage.job_uuid}
                                                            aria-label={isExpanded ? 'ocultar detalhes' : 'ver detalhes'}
                                                            aria-expanded={isExpanded}
                                                        >
                                                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>{usage.job_file_name || '—'}</TableCell>
                                                    <TableCell>{usage.printer_name || '—'}</TableCell>
                                                    <TableCell>
                                                        <Chip label={statusLabel(usage.job_status)} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="right">{formatGrams(usage.grams)}</TableCell>
                                                    <TableCell>
                                                        {formatDate(usage.job_finished_at || usage.job_started_at || usage.created_at)}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={6}
                                                        sx={{
                                                            py: 0,
                                                            borderBottom: isExpanded ? undefined : 'none',
                                                            bgcolor: isExpanded ? 'action.hover' : undefined,
                                                        }}
                                                    >
                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                            <JobDetails
                                                                state={jobDetails[usage.job_uuid]}
                                                                filamentId={filamentId}
                                                            />
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <CustomPagination
                                    currentPage={page}
                                    totalPages={totalPages}
                                    onPageChange={setPage}
                                />
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default FilamentUsageDialog;
